import { Navigate, createBrowserRouter } from 'react-router-dom'
import type { ComponentType } from 'react'
import { ProtectedLayout } from './layout/protected-layout'
import { RouteErrorPage } from './route-error-page'

const lazyPage =
  <T extends Record<string, unknown>>(loader: () => Promise<T>, exportName: keyof T) =>
  async () => {
    const module = await loader()
    return { Component: module[exportName] as ComponentType }
  }

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      <img src="/brand/yihang-logo.png" alt="" />
      <span>正在加载页面…</span>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    ErrorBoundary: RouteErrorPage,
    HydrateFallback: RouteFallback,
    lazy: lazyPage(() => import('./pages/auth/login-page'), 'LoginPage'),
  },
  {
    path: '/activate',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/',
    Component: ProtectedLayout,
    ErrorBoundary: RouteErrorPage,
    HydrateFallback: RouteFallback,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        lazy: lazyPage(() => import('./pages/dashboard-page'), 'DashboardPage'),
      },
      {
        path: 'data-screen',
        lazy: lazyPage(() => import('./pages/data-screen-page'), 'DataScreenPage'),
      },
      {
        path: 'plugin-download',
        lazy: lazyPage(() => import('./pages/plugin-page'), 'PluginPage'),
      },
      { path: 'shops', lazy: lazyPage(() => import('./pages/shop-authorization-page'), 'ShopAuthorizationPage') },
      { path: 'wb', lazy: lazyPage(() => import('./pages/wb-page'), 'WbPage') },
      { path: 'store-performance', lazy: lazyPage(() => import('./pages/store-performance-page'), 'StorePerformancePage') },
      { path: 'products', lazy: lazyPage(() => import('./pages/products-page'), 'ProductsPage') },
      {
        path: 'image-to-psd',
        lazy: lazyPage(() => import('./pages/image-to-psd-page'), 'ImageToPsdPage'),
      },
      { path: 'image-rewrites', lazy: lazyPage(() => import('./pages/standalone-image-page'), 'StandaloneImagePage') },
      {
        path: 'store-collection',
        lazy: lazyPage(() => import('./pages/store-collection-page'), 'StoreCollectionPage'),
      },
      {
        path: 'auto-selection/rules',
        lazy: lazyPage(() => import('./pages/auto-selection-rules-page'), 'AutoSelectionRulesPage'),
      },
      {
        path: 'auto-selection/products',
        lazy: lazyPage(
          () => import('./pages/auto-selection-products-page'),
          'AutoSelectionProductsPage',
        ),
      },
      {
        path: 'products/:id/edit',
        lazy: lazyPage(() => import('./pages/product-editor-page'), 'ProductEditorPage'),
      },
      {
        path: 'validation',
        lazy: lazyPage(() => import('./pages/validation-page'), 'ValidationPage'),
      },
      {
        path: 'published',
        lazy: lazyPage(() => import('./pages/published-page'), 'PublishedPage'),
      },
      {
        path: 'ozon-calculator',
        lazy: lazyPage(() => import('./pages/ozon-calculator-page'), 'OzonCalculatorPage'),
      },
      {
        path: 'pricing-calculator',
        lazy: lazyPage(() => import('./pages/calculator-page'), 'PricingCalculatorPage'),
      },
      {
        path: 'profit-calculator',
        lazy: lazyPage(() => import('./pages/calculator-page'), 'ProfitCalculatorPage'),
      },
      {
        path: 'publish-result/:taskId',
        lazy: lazyPage(() => import('./pages/publish-result-page'), 'PublishResultPage'),
      },
      { path: 'tasks', element: <Navigate to="/dashboard" replace /> },
      { path: 'orders', lazy: lazyPage(() => import('./pages/orders-page'), 'OrdersPage') },
      {
        path: 'ai-rewrite-logs',
        lazy: lazyPage(() => import('./pages/ai-rewrite-logs-page'), 'AiRewriteLogsPage'),
      },
      {
        path: 'accounts',
        lazy: lazyPage(() => import('./pages/tenant-accounts-page'), 'TenantAccountsPage'),
      },
      {
        path: 'point-ledger',
        lazy: lazyPage(() => import('./pages/tenant-point-ledger-page'), 'TenantPointLedgerPage'),
      },
      {
        path: 'orders/:id',
        lazy: lazyPage(() => import('./pages/order-detail-page'), 'OrderDetailPage'),
      },
      { path: 'settings', lazy: lazyPage(() => import('./pages/settings-page'), 'SettingsPage') },
      {
        path: 'notifications',
        lazy: lazyPage(() => import('./pages/notifications-page'), 'NotificationsPage'),
      },
      {
        path: 'messages',
        lazy: lazyPage(() => import('./pages/messages-page'), 'MessagesPage'),
      },
    ],
  },
  {
    path: '*',
    ErrorBoundary: RouteErrorPage,
    HydrateFallback: RouteFallback,
    lazy: lazyPage(() => import('./pages/not-found-page'), 'NotFoundPage'),
  },
])
